import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import moment from 'moment';
import { Button, Space } from '@arkestro/arkestro-design-system';
import { DeleteOutlined } from '@ant-design/icons';
import useTranslation from 'next-translate/useTranslation';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NotificationsContext } from '@components/providers/NotificationsProvider/NotificationsProvider';
import NotificationsListItemIcon
  from '@components/layout/Header/Notifications/NotificationsListItemIcon/NotificationsListItemIcon';
import {
  ActionData,
  actionProps,
  Notification,
  NotificationActionsMap,
  NotificationsContextType,
} from '@components/layout/Header/Notifications/types';
import { determineAction } from '@components/layout/Header/Notifications/utils';
import { GeneralContext } from '@components/providers/GeneralProvider/GeneralProvider';
import { markSeen } from '@utils/StreamNotificationUtils';

import style from './NotificationsListItem.module.scss';

interface Props {
  allList: Notification[];
  item: Notification;
  setAllList: Dispatch<SetStateAction<Notification[]>>;
}

const NotificationsListItem = ({
  item,
  allList,
  setAllList,
}: Props): JSX.Element | null => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const generalContext = useContext(GeneralContext);
  const { setProperty, streamNotificationsFeed: feed } = useContext(NotificationsContext) as NotificationsContextType;
  const [isNew, setIsNew] = useState(false);
  const flags = useFlags();

  useEffect(() => {
    setIsNew(!item?.is_seen);
  }, [item?.is_seen]);

  if (!item?.created_at) return null;

  const formattedTimeStamp = moment.utc(item.created_at).local();

  const actions = Object.keys(item.actions_object).map((key: string) => {
    const rawActionData =
      item?.actions_object[key as keyof NotificationActionsMap];

    const data: ActionData = {
      type: key,
      ...rawActionData,
      onClick: determineAction(key) as (params: actionProps) => void,
      title: rawActionData?.title?.replace(/\w\S*/g, txt =>
        txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) || '',
    };

    if (['accepted_company_invite', 'accepted_request_invite'].includes(key)) {
      data['answer'] = 'accepted';
    } else if (
      ['rejected_company_invite', 'rejected_request_invite'].includes(key)
    ) {
      data['answer'] = 'rejected';
    }

    return data;
  });

  const updateUnseenCount = () => {
    const newList = allList.filter(
      (notify: Notification) =>
        !notify.is_seen && item.activity_id !== notify.activity_id
    );
    setProperty({ unseenCount: newList.length });
  };

  const handleActionClick = (action: ActionData) => {
    action.onClick({
      allList,
      answer: action.answer,
      feed: feed || undefined,
      flags,
      generalContext,
      item,
      router,
      setAllList,
      t,
    });
    updateUnseenCount();
  };

  const markNotificationAsSeen = () => {
    if (feed) {
      markSeen(feed, [item.activity_id as string]);
    }
    setIsNew(false);
    updateUnseenCount();
  };

  return (
    <div className={style.listItem} data-testid='notifications-list-item'>
      <div aria-hidden='true' className={style.icon}>
        <NotificationsListItemIcon iconType={item.action_name} />
      </div>
      <div className={style.listItemContent}>
        <div className={style.header}>
          <h3
            className={style.title}
            data-testid='notifications-list-item-title'
          >
            {isNew && <span className={style.newDot} />}
            {item.title}
          </h3>
          <time
            className={isNew ? style.timestampNew : style.timestamp}
            data-testid='notifications-list-item-timestamp'
            dateTime={formattedTimeStamp.toISOString()}
            title={formattedTimeStamp.toLocaleString()}
          >
            {formattedTimeStamp.fromNow()}
          </time>
        </div>
        <div
          className={style.content}
          data-testid='notifications-list-item-description'
        >
          {item.description}
        </div>
        <div
          className={style.footer}
          data-testid='notifications-list-item-actions'
        >
          <Space>
            {actions.length > 0 &&
              actions.map(
                action =>
                  action.type !== 'cancel' && (
                    <Button
                      key={`notification-action-${action.type}`}
                      onClick={() => handleActionClick(action)}
                      type='link'
                    >
                      {action.title}
                    </Button>
                  )
              )}
            {isNew &&
              !['company_invitation', 'request_invitation'].includes(
                item.action_name
              ) && (
              <Button
                onClick={markNotificationAsSeen}
                type='link'
              >
                {t('notifications.mark_as_read')}
              </Button>
            )}
          </Space>

          {actions.length > 0 &&
            actions.map(
              action =>
                action.type === 'cancel' && (
                  <Button
                    key={`notification-action-${action.type}`}
                    data-testid='notifications-list-item-dismiss'
                    icon={<DeleteOutlined />}
                    onClick={() => handleActionClick(action)}
                    title={action.title}
                    type='text'
                  />
                )
            )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsListItem;
